import { useState, useEffect, useMemo } from "react";
import BaseModal from "@components/modals/BaseModal";
import Button from "@components/ui/buttons/Button";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import TextField from "@components/ui/textfields/TextField";
import DropDown from "@components/ui/dropdown/DropDown";
import CheckBox from "@components/ui/selection/CheckBox";
import Icon1 from "@assets/icons/Icon";
import { paymentsApi } from "@services/api/payments";
import { showNotification } from "context/notification.service";
import { useDataStore } from "@store/user_org";
import type { Ordre } from "types/ordre";

const TYPE_OPTIONS = [
  { value: "acompte", label: "Acompte" },
  { value: "solde", label: "Solde (paiement final)" },
];

const PERCENT_OPTIONS = [
  { value: "custom", label: "Montant personnalisé" },
  { value: "10", label: "10%" },
  { value: "20", label: "20%" },
  { value: "25", label: "25%" },
  { value: "30", label: "30%" },
  { value: "40", label: "40%" },
  { value: "50", label: "50%" },
  { value: "60", label: "60%" },
  { value: "70", label: "70%" },
  { value: "75", label: "75%" },
  { value: "80", label: "80%" },
  { value: "90", label: "90%" },
  { value: "100", label: "100%" },
];

interface Props {
  ordre: Ordre;
  defaultType: "acompte" | "solde";
  onClose: () => void;
  onSuccess: () => void;
}

export default function PaymentRequestModal({ ordre, defaultType, onClose, onSuccess }: Props) {
  const orgData = useDataStore((s) => s.orgData);

  const [type, setType] = useState<string>(defaultType);
  const [amount, setAmount] = useState(ordre.amount ? String(ordre.amount) : "");
  const [percentOption, setPercentOption] = useState<string>("custom");
  const [clientName, setClientName] = useState(
    ordre.contacts?.[0]?.nom || ""
  );
  const [clientFirstName, setClientFirstName] = useState(
    ordre.contacts?.[0]?.prenom || ""
  );
  const [clientEmail, setClientEmail] = useState(
    ordre.contacts?.[0]?.email || ""
  );
  const [clientPhone, setClientPhone] = useState(
    ordre.contacts?.[0]?.phone || ""
  );
  const [loading, setLoading] = useState(false);
  const [attachInvoice, setAttachInvoice] = useState(false);

  const factureDoc = useMemo(
    () => (ordre.documents || []).find((d) => d.type === "facture" && d.gcs_path),
    [ordre.documents],
  );

  const bridgeConnected = orgData?.bridge?.connected;
  const isSolde = type === "solde";
  const soldeWithoutFacture = isSolde && !factureDoc;

  const totalDevis = ordre.amount ?? 0;

  // When percent option changes, recalculate amount
  useEffect(() => {
    if (percentOption === "custom" || type !== "acompte" || totalDevis <= 0) return;
    const pct = parseFloat(percentOption);
    if (!isNaN(pct)) {
      setAmount(String(Math.round((totalDevis * pct / 100) * 100) / 100));
    }
  }, [percentOption, totalDevis, type]);

  // Auto-attach invoice when requesting solde
  useEffect(() => {
    if (type === "solde" && factureDoc) setAttachInvoice(true);
  }, [type, factureDoc]);

  const amountExceedsTotal = type === "acompte" && totalDevis > 0 && parseFloat(amount) > totalDevis;

  const handleSubmit = async () => {
    if (!amount || !clientEmail || !clientName) {
      showNotification({ type: "error", message: "Veuillez remplir tous les champs obligatoires" });
      return;
    }

    if (amountExceedsTotal) {
      showNotification({ type: "error", message: "Le montant de l'acompte ne peut pas dépasser le montant total des devis" });
      return;
    }

    setLoading(true);
    try {
      await paymentsApi.create({
        ordre_id: ordre.id,
        type: type as "acompte" | "solde",
        amount: parseFloat(amount),
        reglement_infos: {
          email: clientEmail,
          name: clientName,
          first_name: clientFirstName || undefined,
          phone: clientPhone || undefined,
        },
        invoice_gcs_path: attachInvoice && factureDoc?.gcs_path ? factureDoc.gcs_path : undefined,
      });
      showNotification({ type: "success", message: "Demande de paiement envoyée" });
      onSuccess();
      onClose();
    } catch (err: any) {
      const detail = err?.response?.data?.detail || "Erreur lors de la création du paiement";
      showNotification({ type: "error", message: detail });
    } finally {
      setLoading(false);
    }
  };

  const formatAmount = (v: number) => v.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <BaseModal
      title="Demander un paiement"
      startIcon={<Icon1 name="payments" size={20} />}
      onClose={onClose}
      width="max-w-md"
    >
      <div className="flex flex-col gap-3 p-4">
        {!bridgeConnected && (
          <div
            className="flex flex-row items-center gap-2 p-3 rounded-lg"
            style={{ background: "#FEF9C3", border: "1px solid #FDE68A" }}
          >
            <span
              className="text-h3"
              style={{ fontFamily: "Poppins", color: "#92400E" }}
            >
              Bridge n'est pas configuré. Allez dans Paramètres &gt; Paiements pour configurer votre IBAN.
            </span>
          </div>
        )}

        <DropDown
          label="Type de paiement"
          options={TYPE_OPTIONS}
          value={type}
          onSelect={(v) => setType(String(v))}
        />

        {/* Percentage selector for acompte */}
        {type === "acompte" && totalDevis > 0 && (
          <DropDown
            label="Pourcentage de l'acompte"
            options={PERCENT_OPTIONS}
            value={percentOption}
            onSelect={(v) => setPercentOption(String(v))}
          />
        )}

        <div className="flex flex-col gap-1">
          <TextField
            label="Montant (EUR) *"
            type="number"
            value={amount}
            onChange={(v) => {
              setAmount(v);
              // If user manually edits, switch to custom
              if (percentOption !== "custom") setPercentOption("custom");
            }}
          />
          {amountExceedsTotal && (
            <span className="text-h3" style={{ fontFamily: "Poppins", color: "#DC2626" }}>
              Le montant ne peut pas dépasser le total des devis ({formatAmount(totalDevis)} EUR)
            </span>
          )}
        </div>

        <TextField
          label="Nom du client *"
          value={clientName}
          onChange={setClientName}
        />

        <TextField
          label="Prénom du client"
          value={clientFirstName}
          onChange={setClientFirstName}
        />

        <TextField
          label="Email du client *"
          value={clientEmail}
          onChange={setClientEmail}
        />

        <TextField
          label="Téléphone du client"
          value={clientPhone}
          onChange={setClientPhone}
        />

        {soldeWithoutFacture && (
          <div
            className="flex flex-row items-center gap-2 p-3 rounded-lg"
            style={{ background: "#FEF3C7", border: "1px solid #FDE68A" }}
          >
            <Icon1 name="warning" size={16} color="#92400E" />
            <span className="text-h3" style={{ color: "#92400E" }}>
              Aucune facture n'est jointe à cet ordre. Il est recommandé d'ajouter une facture avant de demander le solde.
            </span>
          </div>
        )}

        {factureDoc && (
          <div className="flex flex-row items-center gap-2 pt-1">
            <CheckBox
              checked={attachInvoice}
              onChange={(e) => setAttachInvoice(e.target.checked)}
            />
            <span className="text-h3 text-neutral-600">
              Joindre la facture ({factureDoc.name}) à l'email
            </span>
          </div>
        )}
      </div>

      <div className="border-t-1 p-4 flex flex-row justify-end gap-2">
        <SecondaryButton variant="small" onClick={onClose}>
          Annuler
        </SecondaryButton>
        <Button
          variant="small"
          label={loading ? "Envoi..." : "Envoyer la demande"}
          onClick={handleSubmit}
          disabled={loading || !bridgeConnected || amountExceedsTotal}
        />
      </div>
    </BaseModal>
  );
}
